// Where a data point exists (metrics.yaml `coverage:`): the whole U.S., or only a region whose state and local
// sources publish it. The build checks it (ingest/checks.ts) and the app reads it instead of guessing from blanks.
// The regions are shared/regions.ts REGIONS; this adds "us" and the words.
import { REGIONS, type Region } from "./regions.ts";

export type Coverage = "us" | keyof typeof REGIONS;
export const COVERAGES: readonly Coverage[] = ["us", ...(Object.keys(REGIONS) as Array<keyof typeof REGIONS>)];

/** Each coverage's areas: by state (FIPS) or by county (FIPS); the U.S. is everywhere. `only` reads "California only". */
export const COVERAGE: Record<Coverage, { name: string; only: string | null; states?: readonly string[]; counties?: readonly string[] }> = {
  us: { name: "U.S.", only: null },
  ...(Object.fromEntries(Object.entries(REGIONS).map(([id, r]: [string, Region]) => [id, { ...r, only: `${r.name} only` }])) as
    Record<keyof typeof REGIONS, { name: string; only: string; states: readonly string[]; counties?: readonly string[] }>),
};

/** SQL: whether a `geo` row (alias g) lies inside a coverage; the same rule as covers(). */
export function coversSql(c: Coverage, g = "g"): string {
  const d = COVERAGE[c];
  const list = (xs: readonly string[]) => xs.map((x) => `'${x}'`).join(", ");
  const st = `coalesce(${g}.state_fips, ${g}.main_state_fips)`; // a metro by its main state
  return [d.states && `(${st} IS NULL OR ${st} IN (${list(d.states)}))`,
    d.counties && `(${g}.county_fips IS NULL OR ${g}.county_fips IN (${list(d.counties)}))`].filter(Boolean).join(" AND ") || "TRUE";
}

export const isCoverage = (s: unknown): s is Coverage => (COVERAGES as readonly unknown[]).includes(s);

/** Whether an area (its state and county FIPS) lies inside a coverage. Areas without a county (states, metros) are judged by state only. */
export function covers(c: Coverage, area: { state_fips?: string | null; main_state_fips?: string | null; county_fips?: string | null }): boolean {
  const d = COVERAGE[c];
  const st = area.state_fips ?? area.main_state_fips;
  return (!d.states || st == null || d.states.includes(st))
    && (!d.counties || area.county_fips == null || d.counties.includes(area.county_fips));
}
